import { Injectable, OnDestroy, OnInit } from '@angular/core'
import { ActivatedRoute, NavigationEnd, NavigationStart, Router } from '@angular/router'
import { captureException } from '@sentry/browser'
import { TrackingService } from 'app/_jaettu-angular/tracking'
import { EnvironmentType } from 'app/app.environment'
import { environment } from 'environments/environment'
import { Subject, combineLatest, filter, firstValueFrom, startWith, takeUntil } from 'rxjs'
import { TimestampService } from '../../../_jaettu-angular/service/timestamp-service'
import { LogienLahetystiedotPyynto, LogienLahetystiedotVastaus } from '../../../_jaettu-lemonator/model/logging'
import { AsiakasService } from '../asiakas/asiakas.service'
import { FirebaseLemonator } from '../firebase-lemonator.service'
import { KirjautunutKayttajaService } from '../kirjautunut-kayttaja.service'

@Injectable({
  providedIn: 'root',
})
export class TrackingServiceLemonatorImpl implements TrackingService, OnInit, OnDestroy {
  private ngUnsubscribe: Subject<void> = new Subject<void>()
  private lastNavigationMethod: 'link/url' | 'back/forward'

  constructor(
    private _router: Router,
    private route: ActivatedRoute,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    private _asiakasService: AsiakasService,
    private _timeStampService: TimestampService,
    private _firebaseLemonator: FirebaseLemonator,
  ) {
    combineLatest([
      this._kirjautunutKayttajaService.kirjanpitajanTiedotObservable,
      this._asiakasService.nykyinenAsiakasAvainObservable.pipe(startWith(null)),
      this._router.events.pipe(filter(event => event instanceof NavigationStart || event instanceof NavigationEnd)),
    ])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(([kirjanpitajanTiedot, asiakas, event]) => {
        if (!kirjanpitajanTiedot || !event) {
          return
        }
        if (event instanceof NavigationStart) {
          this.determineNavigationMethod(event)
        } else if (event instanceof NavigationEnd) {
          const routeData = this.route.snapshot.firstChild?.data
          this.logNavigation(event.urlAfterRedirects, routeData?.description ?? null, asiakas?.avain ?? null, kirjanpitajanTiedot.uid)
        }
      })
  }

  ngOnInit(): void {}

  logNavigation(url: string, description: string, asiakasAvain: string | null, kirjanpitajaUid: string) {
    const production = this.environmentCheck()
    if (production) {
      const lahetysTiedot: LogienLahetystiedotPyynto = {
        kirjanpitajaUid: kirjanpitajaUid,
        asiakasAvain: asiakasAvain,
        navigationEvent: {
          eventType: 'navigoi' /* Navigointityyppi */,
          navigationMethod: this.lastNavigationMethod /* Navigointitapa */,
          seen: description /* Mitä tietoja nähtiin */,
        },
        url: url,
        timestamp: this._timeStampService.now(),
      }
      this.sendLogs(lahetysTiedot)
    }
  }

  async logAction(actionId: string) {
    const production = this.environmentCheck()
    if (production) {
      const kirjanpitajanTiedot = await firstValueFrom(this._kirjautunutKayttajaService.kirjanpitajanTiedotObservable)
      if (!kirjanpitajanTiedot) {
        return
      }
      const asiakas = await firstValueFrom(this._asiakasService.nykyinenAsiakasObservable)

      const lahetysTiedot: LogienLahetystiedotPyynto = {
        kirjanpitajaUid: kirjanpitajanTiedot.uid,
        asiakasAvain: asiakas?.avain,
        navigationEvent: {
          eventType: 'klikkasi toimintoa' /* Navigointityyppi */,
          eventName: actionId /* Mikä toiminto tehtiin */,
        },
        url: this._router.url,
        timestamp: this._timeStampService.now(),
      }
      this.sendLogs(lahetysTiedot)
    }
  }

  environmentCheck(): boolean {
    return environment.environment === EnvironmentType.PRODUCTION
  }

  private determineNavigationMethod(event: NavigationStart) {
    if (event.navigationTrigger === 'imperative') {
      this.lastNavigationMethod = 'link/url'
    } else if (event.navigationTrigger === 'popstate') {
      this.lastNavigationMethod = 'back/forward'
    }
  }

  async sendLogs(lahetysTiedot: LogienLahetystiedotPyynto) {
    try {
      const resp = await this._firebaseLemonator.functionsCall<LogienLahetystiedotPyynto, LogienLahetystiedotVastaus>('sendLogsToCloudLogging', lahetysTiedot)
      /** Käytetään captureException virheiden kirjaamiseen Sentryyn eikä Angularin ErrorHandleria, jotteivät virheet aiheuttaisi sovelluksen kaatumista */
      if (!resp) {
        captureException(new Error('Ei vastausta lähetettäessä logitietoja!'))
      } else if (resp.e) {
        captureException(new Error('Virhe lähetettäessä logitietoja: ' + resp.e))
      }
    } catch (err) {
      captureException(err)
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next()
    this.ngUnsubscribe.complete()
  }
}
